import { Trans } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiPostBillingUsers, PaymentUserOut } from "../../../api";
import { CreatePaymentUserRequestSchema } from "../../../api/generated/schemas.gen";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
} from "../../../components/ui-kit/dialog";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { PasswordInput } from "../../../components/ui-kit/fields/password-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormField } from "../../../components/ui-kit/form/form-field";
import { FormGrid } from "../../../components/ui-kit/form/form-grid";
import {
  ADD_DIALOG_CLASS,
  ADD_DIALOG_WIDTH,
  RecordDialogTitle,
} from "../../../components/ui-kit/record-dialog-chrome";
import { InfoTable } from "../../../components/ui-kit/tables/info-table";

/** The backend's starting balance and bounds, prefilled and editable per user. */
const INITIAL_CREDITS =
  CreatePaymentUserRequestSchema.properties.initial_credits;

/**
 * Creates a paid HF-screening user: `POST /billing/users` sets up the login,
 * a placeholder patient, the physician record linking the two and a billing
 * account with the chosen starting credits in one call. Only the username,
 * password, email and credits are asked for; the backend prefills the rest
 * (name, country, date of birth...). Every step either completes or fails the request, so a
 * 201 means the account is ready to use.
 */
export const AddScreeningUserDialog = observer(function AddScreeningUserDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [created, setCreated] = useState<PaymentUserOut | null>(null);
  const [fields] = useState(() =>
    observable({
      username: "",
      password: "",
      email: "",
      initial_credits: INITIAL_CREDITS.default,
    }),
  );

  async function save() {
    const res = await apiPostBillingUsers({
      body: fields,
    });
    onSaved();
    setCreated(res.data);
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className={ADD_DIALOG_CLASS}
      width={ADD_DIALOG_WIDTH}
    >
      <RecordDialogTitle>
        <Trans>New screening user</Trans>
      </RecordDialogTitle>

      {created ? (
        <DialogBody className="flex flex-col gap-4">
          <div className="border-success-border bg-success-soft text-success-foreground rounded-lg border p-2 text-sm">
            <Trans>{created.username} is fully set up and ready to use.</Trans>
          </div>

          <InfoTable>
            <tr>
              <td>
                <Trans>Username</Trans>
              </td>
              <td>{created.username}</td>
            </tr>
            <tr>
              <td>
                <Trans>Role</Trans>
              </td>
              <td>{created.role}</td>
            </tr>
            <tr>
              <td>
                <Trans>Patient</Trans>
              </td>
              <td>{created.patient_id}</td>
            </tr>
            <tr>
              <td>
                <Trans>Physician</Trans>
              </td>
              <td>{created.physician_uuid}</td>
            </tr>
            <tr>
              <td>
                <Trans>Credits</Trans>
              </td>
              <td>{created.account.balance}</td>
            </tr>
          </InfoTable>
        </DialogBody>
      ) : (
        <Form className="flex flex-col min-h-0 flex-1" onSubmit={save}>
          <DialogBody className="flex flex-col gap-4">
            <FormGrid>
              <FormField label={<Trans>Username</Trans>} mandatory>
                <TextInput attr="username" model={fields} required />
              </FormField>
              <FormField label={<Trans>Password</Trans>} mandatory>
                <PasswordInput attr="password" model={fields} required />
              </FormField>
              <FormField label={<Trans>Email</Trans>} mandatory>
                <TextInput attr="email" model={fields} required />
              </FormField>
              <FormField label={<Trans>Credits</Trans>} mandatory>
                <NumberInput
                  attr="initial_credits"
                  model={fields}
                  required
                  min={INITIAL_CREDITS.minimum}
                  max={INITIAL_CREDITS.maximum}
                  step={1}
                  width={120}
                />
              </FormField>
            </FormGrid>
            <p className="text-muted-foreground text-xs">
              <Trans>
                Pass the password on to the customer and ask them to change it,
                or let them use "Forgot password" with this email.
              </Trans>
            </p>
          </DialogBody>

          <DialogFooter>
            <Button type="button" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button variant="primary" type="submit">
              <Trans>Create screening user</Trans>
            </Button>
          </DialogFooter>
        </Form>
      )}
    </Dialog>
  );
});
